import { useSignal } from '@preact/signals';

export type Props = {
	initialFocusIndex?: number;
	focusCycling?: boolean;
	numItems: number;
	allowSelectWithSpace?: boolean;
	onEscape?: (event: KeyboardEvent | undefined, index: number) => void;
	onSelect?: (event: KeyboardEvent | undefined, index: number) => void;
	onFocusChange?: (newIndex: number, prevIndex: number) => void;
	ariaParentId?: string;
	ariaOptionId?: string;
};

/**
 * A set of functions to control a dropdown with a keyboard. Comes equipped with basic controls such as:
 * - Arrow Up/Down to navigate through the dropdown
 * - Enter to select an item
 * - Space to select an item
 * - Escape to close the dropdown
 * - Home/End to navigate to the first/last item
 * Also includes status for aria compliant stuff.
 *
 * The hook exposes `onKeyDown` function that should be called in the onKeyDown event of the dropdown container and
 * passed the keyboard event. There are other `handleX` methods to allow you to control the dropdown from outside
 * imperatively.
 */
export function useKeyboardControls({
	allowSelectWithSpace = true,
	focusCycling = true,
	numItems,
	onSelect,
	onEscape,
	onFocusChange,
}: Props) {
	const focusedIndex = useSignal<number>(0);

	function setFocus(index: number) {
		if (index !== focusedIndex.peek()) {
			onFocusChange?.(index, focusedIndex.peek());
			focusedIndex.value = index;
		}
	}

	function focusPrevious() {
		if (focusedIndex.peek() <= 0) {
			if (focusCycling) {
				setFocus(numItems - 1);
			} else {
				setFocus(0);
			}
		} else {
			setFocus(focusedIndex.peek() ? focusedIndex.peek() - 1 : 0);
		}
	}

	function focusNext() {
		if (focusedIndex.peek() >= numItems - 1) {
			if (focusCycling) {
				setFocus(0);
			} else {
				setFocus(numItems - 1);
			}
		} else {
			setFocus(focusedIndex.peek() === null ? 0 : focusedIndex.peek() + 1);
		}
	}

	function handleSelect(event?: KeyboardEvent) {
		onSelect?.(event, focusedIndex.value);
	}

	function handleEscape(event?: KeyboardEvent) {
		onEscape?.(event, focusedIndex.value);
	}

	function onKeyDown(event: KeyboardEvent) {
		if (event.code === 'ArrowUp') {
			event.preventDefault();
			focusPrevious();
		} else if (event.code === 'ArrowDown') {
			event.preventDefault();
			focusNext();
		} else if (event.code === 'Enter' || (allowSelectWithSpace && event.code === 'Space')) {
			event.preventDefault();
			handleSelect(event);
		} else if (event.code === 'Escape') {
			event.preventDefault();
			handleEscape(event);
		}
	}

	return {
		focusedIndex: focusedIndex.value,
		onKeyDown,
		setFocus,
		focusPrevious,
		focusNext,
		handleSelect,
		handleEscape,
	};
}
